import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { saveAs } from 'file-saver';
import JSZip from 'jszip';

// Color constants matching app theme
const COLORS = {
  gold: '#D4AF37',
  dark: '#030305',
  white: '#F8F5F2',
  gray: '#A1A1AA',
};

/**
 * Export Sun Map and Analysis as PDF
 */
export const exportAnalysisPDF = async (data, language = 'en') => {
  const { scores, meta_scores, ai_analysis, userName, date } = data;
  
  const pdf = new jsPDF('p', 'mm', 'a4');
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 20;
  let yPos = margin;

  // Helper function to add text with word wrap
  const addWrappedText = (text, x, y, maxWidth, lineHeight = 6) => {
    const lines = pdf.splitTextToSize(text, maxWidth);
    lines.forEach((line, index) => {
      if (y + (index * lineHeight) > pageHeight - margin) {
        pdf.addPage();
        y = margin;
      }
      pdf.text(line, x, y + (index * lineHeight));
    });
    return y + (lines.length * lineHeight);
  };

  // Header
  pdf.setFillColor(3, 3, 5);
  pdf.rect(0, 0, pageWidth, 40, 'F');
  
  pdf.setTextColor(212, 175, 55);
  pdf.setFontSize(24);
  pdf.setFont('helvetica', 'bold');
  pdf.text('IN — Inner Navigation', pageWidth / 2, 20, { align: 'center' });
  
  pdf.setFontSize(12);
  pdf.setTextColor(248, 245, 242);
  pdf.text(language === 'tr' ? 'Güneş Haritası Raporu' : 'Sun Map Report', pageWidth / 2, 30, { align: 'center' });

  yPos = 50;

  // User info
  pdf.setTextColor(60, 60, 60);
  pdf.setFontSize(11);
  pdf.setFont('helvetica', 'normal');
  if (userName) {
    pdf.text(`${language === 'tr' ? 'İsim' : 'Name'}: ${userName}`, margin, yPos);
    yPos += 7;
  }
  pdf.text(`${language === 'tr' ? 'Tarih' : 'Date'}: ${new Date(date || Date.now()).toLocaleDateString()}`, margin, yPos);
  yPos += 15;

  // Domain Scores Section
  pdf.setTextColor(212, 175, 55);
  pdf.setFontSize(14);
  pdf.setFont('helvetica', 'bold');
  pdf.text(language === 'tr' ? '12 Alan Puanları' : '12 Domain Scores', margin, yPos);
  yPos += 10;

  const domainLabels = {
    en: {
      physical_health: 'Physical Health',
      strength_fitness: 'Strength / Fitness',
      energy: 'Energy',
      environment_order: 'Space / Order',
      work_mission: 'Work / Mission',
      money_financial: 'Money / Financial',
      love_relationship: 'Love / Relationship',
      friends_community: 'Friends / Community',
      emotional_stability: 'Emotional Stability',
      discipline_consistency: 'Discipline',
      learning_growth: 'Learning / Growth',
      meaning_purpose: 'Meaning / Purpose',
    },
    tr: {
      physical_health: 'Fiziksel Sağlık',
      strength_fitness: 'Güç / Fitness',
      energy: 'Enerji',
      environment_order: 'Alan / Düzen',
      work_mission: 'İş / Misyon',
      money_financial: 'Para / Finansal',
      love_relationship: 'Aşk / İlişki',
      friends_community: 'Arkadaşlar / Topluluk',
      emotional_stability: 'Duygusal Denge',
      discipline_consistency: 'Disiplin',
      learning_growth: 'Öğrenme / Gelişim',
      meaning_purpose: 'Anlam / Amaç',
    },
  };

  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(60, 60, 60);

  const labels = domainLabels[language] || domainLabels.en;
  const domains = Object.entries(scores || {});
  
  // Two columns for scores
  const colWidth = (pageWidth - 2 * margin) / 2;
  domains.forEach(([key, value], index) => {
    const col = index % 2;
    const row = Math.floor(index / 2);
    const x = margin + col * colWidth;
    const y = yPos + row * 7;
    
    pdf.text(`${labels[key] || key}: ${value}/10`, x, y);
  });
  
  yPos += Math.ceil(domains.length / 2) * 7 + 10;

  // Meta Scores Section
  if (meta_scores) {
    pdf.setTextColor(212, 175, 55);
    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.text(language === 'tr' ? 'Yaşam Yönü Metrikleri' : 'Life Direction Metrics', margin, yPos);
    yPos += 10;

    const metaLabels = {
      en: { direction: 'Direction', alignment: 'Alignment', momentum: 'Momentum', stress: 'Stress' },
      tr: { direction: 'Yön', alignment: 'Uyum', momentum: 'İvme', stress: 'Stres' },
    };

    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(60, 60, 60);

    const mLabels = metaLabels[language] || metaLabels.en;
    Object.entries(meta_scores).forEach(([key, value], index) => {
      pdf.text(`${mLabels[key] || key}: ${value}/10`, margin + (index % 2) * colWidth, yPos + Math.floor(index / 2) * 7);
    });
    yPos += 20;
  }

  // AI Analysis Section
  if (ai_analysis) {
    pdf.setTextColor(212, 175, 55);
    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.text(language === 'tr' ? 'AI Okuma & Rehberlik' : 'AI Reading & Guidance', margin, yPos);
    yPos += 10;

    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(60, 60, 60);

    // Main reading
    if (ai_analysis.main_reading) {
      pdf.setFont('helvetica', 'bold');
      pdf.text(language === 'tr' ? 'Ana Okuma:' : 'Main Reading:', margin, yPos);
      yPos += 6;
      pdf.setFont('helvetica', 'normal');
      yPos = addWrappedText(ai_analysis.main_reading, margin, yPos, pageWidth - 2 * margin);
      yPos += 8;
    }

    // Archetypes
    if (ai_analysis.archetypes && ai_analysis.archetypes.length > 0) {
      pdf.setFont('helvetica', 'bold');
      pdf.text(language === 'tr' ? 'Arketipler:' : 'Archetypes:', margin, yPos);
      yPos += 6;
      pdf.setFont('helvetica', 'normal');
      pdf.text(ai_analysis.archetypes.join(', '), margin, yPos);
      yPos += 10;
    }

    // Priorities
    if (ai_analysis.priorities && ai_analysis.priorities.length > 0) {
      pdf.setFont('helvetica', 'bold');
      pdf.text(language === 'tr' ? 'Odak Öncelikleri:' : 'Focus Priorities:', margin, yPos);
      yPos += 6;
      pdf.setFont('helvetica', 'normal');
      ai_analysis.priorities.forEach((priority, index) => {
        if (yPos > pageHeight - margin) {
          pdf.addPage();
          yPos = margin;
        }
        pdf.text(`${index + 1}. ${priority}`, margin, yPos);
        yPos += 6;
      });
      yPos += 5;
    }

    // Weekly Focus
    if (ai_analysis.weekly_focus) {
      pdf.setFont('helvetica', 'bold');
      pdf.text(language === 'tr' ? 'Haftalık Odak:' : 'Weekly Focus:', margin, yPos);
      yPos += 6;
      pdf.setFont('helvetica', 'normal');
      yPos = addWrappedText(ai_analysis.weekly_focus, margin, yPos, pageWidth - 2 * margin);
      yPos += 8;
    }

    // Habits
    if (ai_analysis.habits && ai_analysis.habits.length > 0) {
      pdf.setFont('helvetica', 'bold');
      pdf.text(language === 'tr' ? 'Önerilen Alışkanlıklar:' : 'Suggested Habits:', margin, yPos);
      yPos += 6;
      pdf.setFont('helvetica', 'normal');
      ai_analysis.habits.forEach((habit, index) => {
        if (yPos > pageHeight - margin) {
          pdf.addPage();
          yPos = margin;
        }
        yPos = addWrappedText(`• ${habit}`, margin, yPos, pageWidth - 2 * margin);
      });
    }
  }

  // Footer
  pdf.setFontSize(8);
  pdf.setTextColor(160, 160, 160);
  pdf.text('Generated by IN — Inner Navigation | sunkinglive.com', pageWidth / 2, pageHeight - 10, { align: 'center' });

  // Save
  const fileName = `sun-map-report-${new Date().toISOString().split('T')[0]}.pdf`;
  pdf.save(fileName);
  return fileName;
};

/**
 * Export Chat History as PDF
 */
export const exportChatPDF = async (messages, language = 'en') => {
  const pdf = new jsPDF('p', 'mm', 'a4');
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 20;
  let yPos = margin;

  // Helper function
  const addWrappedText = (text, x, y, maxWidth, lineHeight = 5) => {
    const lines = pdf.splitTextToSize(text, maxWidth);
    lines.forEach((line, index) => {
      if (y + (index * lineHeight) > pageHeight - margin) {
        pdf.addPage();
        y = margin;
      }
      pdf.text(line, x, y + (index * lineHeight));
    });
    return y + (lines.length * lineHeight) + 3;
  };

  // Header
  pdf.setFillColor(3, 3, 5);
  pdf.rect(0, 0, pageWidth, 35, 'F');
  
  pdf.setTextColor(212, 175, 55);
  pdf.setFontSize(20);
  pdf.setFont('helvetica', 'bold');
  pdf.text('IN — Inner Navigation', pageWidth / 2, 18, { align: 'center' });
  
  pdf.setFontSize(11);
  pdf.setTextColor(248, 245, 242);
  pdf.text(language === 'tr' ? 'Koçluk Sohbet Geçmişi' : 'Coaching Chat History', pageWidth / 2, 28, { align: 'center' });

  yPos = 45;

  // Date
  pdf.setTextColor(120, 120, 120);
  pdf.setFontSize(9);
  pdf.text(`${language === 'tr' ? 'Dışa aktarıldı' : 'Exported'}: ${new Date().toLocaleString()}`, margin, yPos);
  yPos += 10;

  // Messages
  if (messages && messages.length > 0) {
    messages.forEach((msg) => {
      if (yPos > pageHeight - 30) {
        pdf.addPage();
        yPos = margin;
      }

      const isUser = msg.role === 'user';
      
      // Role label
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'bold');
      if (isUser) {
        pdf.setTextColor(212, 175, 55);
      } else {
        pdf.setTextColor(100, 100, 100);
      }
      pdf.text(isUser ? (language === 'tr' ? 'Siz' : 'You') : (language === 'tr' ? 'Koç' : 'Coach'), margin, yPos);
      yPos += 5;

      // Message content
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(60, 60, 60);
      yPos = addWrappedText(msg.content, margin, yPos, pageWidth - 2 * margin);
      yPos += 5;
    });
  } else {
    pdf.setTextColor(120, 120, 120);
    pdf.setFontSize(11);
    pdf.text(language === 'tr' ? 'Sohbet geçmişi bulunamadı.' : 'No chat history found.', margin, yPos);
  }

  // Footer
  pdf.setFontSize(8);
  pdf.setTextColor(160, 160, 160);
  pdf.text('Generated by IN — Inner Navigation | sunkinglive.com', pageWidth / 2, pageHeight - 10, { align: 'center' });

  const fileName = `coaching-chat-${new Date().toISOString().split('T')[0]}.pdf`;
  pdf.save(fileName);
  return fileName;
};

/**
 * Export Chat History as TXT
 */
export const exportChatTXT = (messages, language = 'en') => {
  let content = `IN — Inner Navigation\n`;
  content += `${language === 'tr' ? 'Koçluk Sohbet Geçmişi' : 'Coaching Chat History'}\n`;
  content += `${language === 'tr' ? 'Dışa aktarıldı' : 'Exported'}: ${new Date().toLocaleString()}\n`;
  content += `${'='.repeat(50)}\n\n`;

  if (messages && messages.length > 0) {
    messages.forEach((msg) => {
      const role = msg.role === 'user' 
        ? (language === 'tr' ? 'Siz' : 'You') 
        : (language === 'tr' ? 'Koç' : 'Coach');
      content += `[${role}]\n${msg.content}\n\n`;
    });
  } else {
    content += language === 'tr' ? 'Sohbet geçmişi bulunamadı.' : 'No chat history found.';
  }

  const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
  const fileName = `coaching-chat-${new Date().toISOString().split('T')[0]}.txt`;
  saveAs(blob, fileName);
  return fileName;
};

/**
 * Export All User Data as ZIP
 */
export const exportAllDataZIP = async (userData, language = 'en') => {
  const zip = new JSZip();
  const { assessments, chatHistory, profile } = userData;

  // Add profile info
  if (profile) {
    const profileContent = JSON.stringify(profile, null, 2);
    zip.file('profile.json', profileContent);
  }

  // Add assessments
  if (assessments && assessments.length > 0) {
    const assessmentsFolder = zip.folder('assessments');
    
    assessments.forEach((assessment, index) => {
      const date = new Date(assessment.timestamp || assessment.created_at).toISOString().split('T')[0];
      const fileName = `assessment-${date}-${index + 1}.json`;
      assessmentsFolder.file(fileName, JSON.stringify(assessment, null, 2));
    });

    // Also create a summary text file
    let summaryContent = `IN — Inner Navigation\n`;
    summaryContent += `${language === 'tr' ? 'Değerlendirme Geçmişi' : 'Assessment History'}\n`;
    summaryContent += `${'='.repeat(50)}\n\n`;

    assessments.forEach((assessment, index) => {
      const date = new Date(assessment.timestamp || assessment.created_at).toLocaleDateString();
      summaryContent += `${language === 'tr' ? 'Değerlendirme' : 'Assessment'} #${index + 1} - ${date}\n`;
      summaryContent += `-`.repeat(30) + `\n`;
      
      if (assessment.scores) {
        Object.entries(assessment.scores).forEach(([key, value]) => {
          summaryContent += `${key}: ${value}/10\n`;
        });
      }
      summaryContent += `\n`;
    });

    assessmentsFolder.file('summary.txt', summaryContent);
  }

  // Add chat history
  if (chatHistory && chatHistory.length > 0) {
    let chatContent = `IN — Inner Navigation\n`;
    chatContent += `${language === 'tr' ? 'Koçluk Sohbet Geçmişi' : 'Coaching Chat History'}\n`;
    chatContent += `${'='.repeat(50)}\n\n`;

    chatHistory.forEach((msg) => {
      const role = msg.role === 'user' 
        ? (language === 'tr' ? 'Siz' : 'You') 
        : (language === 'tr' ? 'Koç' : 'Coach');
      chatContent += `[${role}]\n${msg.content}\n\n`;
    });

    zip.file('coaching-chat-history.txt', chatContent);
    zip.file('coaching-chat-history.json', JSON.stringify(chatHistory, null, 2));
  }

  // Generate and download ZIP
  const content = await zip.generateAsync({ type: 'blob' });
  const fileName = `in-inner-navigation-data-${new Date().toISOString().split('T')[0]}.zip`;
  saveAs(content, fileName);
  return fileName;
};

/**
 * Capture element as image and return blob
 */
export const captureElementAsBlob = async (elementId) => {
  const element = document.getElementById(elementId);
  if (!element) return null;

  const canvas = await html2canvas(element, {
    backgroundColor: '#030305',
    scale: 2,
  });

  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      resolve(blob);
    }, 'image/png');
  });
};
